import { Link } from "@tanstack/react-router";
import { type MouseEvent, type ReactNode, forwardRef } from "react";

/**
 * An internal link whose target is only known at runtime.
 *
 * Most navigation in Hub should use the typed `<Link to="/…" params={…}>`
 * — it's checked against the route tree. But a lot of destinations are
 * built by string helpers (`projectHref`, `workItemHref`, …) because they
 * render GitLab-shaped paths (`/{group}/{project}/-/work_items/{iid}`)
 * assembled from data. Those can't be expressed as a typed route + params
 * without re-deriving the segments at every call site.
 *
 * Historically they were plain `<a href>`, which the browser treats as a
 * full page load: React is torn down, the `_app` layout loader refetches
 * `/api/me/layout`, and the live socket reconnects — all to render a page
 * that differs from the current one by one panel. `AppLink` keeps those
 * links client-side, so only the changed route segment re-renders.
 *
 * The cast is the whole point of this component: it's the one place that
 * escapes the typed-route check, so a runtime string can't silently spread
 * back into the codebase. Reach for the typed `<Link>` whenever the
 * destination is statically known.
 */
type Props = {
  href: string;
  children: ReactNode;
  // Explicit `| undefined` so callers can forward an optional handler
  // straight through under `exactOptionalPropertyTypes`.
  className?: string | undefined;
  title?: string | undefined;
  // Takes the event so a link inside a draggable/clickable card can
  // stop propagation without falling back to a plain anchor.
  onClick?: ((e: MouseEvent<HTMLAnchorElement>) => void) | undefined;
  "aria-label"?: string | undefined;
  "data-testid"?: string | undefined;
  /** `false` stops a card-shaped link from ghost-dragging in a scroller. */
  draggable?: boolean | undefined;
};

/**
 * Anything else the caller (or a wrapper) put on the element is passed
 * through, and the ref reaches the anchor.
 *
 * That matters for the `asChild` pattern: Radix clones its child and injects
 * event handlers, `aria-describedby`, `data-state` and a ref onto it. This
 * component used to name the handful of props it forwarded and drop the rest,
 * so a `<TooltipTrigger asChild><AppLink …/></TooltipTrigger>` rendered a
 * perfectly good link with a tooltip that could never open — the description
 * preview on the work-items list was dead this way.
 */
export const AppLink = forwardRef<HTMLAnchorElement, Props>(function AppLink(
  { href, children, className, title, onClick, draggable, ...rest },
  ref,
) {
  return (
    <Link
      ref={ref}
      to={href as never}
      className={className}
      title={title}
      onClick={onClick}
      draggable={draggable}
      {...rest}
    >
      {children}
    </Link>
  );
});
